// src/components/pages/Checkout.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector, useDispatch } from 'react-redux';
import { 
  FaArrowLeft, 
  FaCreditCard, 
  FaWallet, 
  FaMobileAlt, 
  FaMoneyBillWave,
  FaCheckCircle,
  FaLock,
  FaTruck,
  FaClock,
  FaShieldAlt
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';
import { confirmOrder, clearSelection } from '../../redux/slices/tiffinSlice';

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderItems, totalPrice } = useSelector((state) => state.tiffin);
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const [selectedPayment, setSelectedPayment] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderId] = useState(`FTS-${Date.now().toString().slice(-6)}`);

  const paymentMethods = [
    { id: 'card', icon: <FaCreditCard />, label: 'Credit/Debit Card', desc: 'Visa, Mastercard, RuPay' },
    { id: 'upi', icon: <FaWallet />, label: 'UPI', desc: 'Google Pay, PhonePe, Paytm' },
    { id: 'netbanking', icon: <FaMobileAlt />, label: 'Net Banking', desc: 'All major banks' },
    { id: 'cod', icon: <FaMoneyBillWave />, label: 'Cash on Delivery', desc: 'Pay when you receive' },
  ];

  // Group items by date
  const groupedItems = orderItems.reduce((acc, item) => {
    const date = item.scheduledDate || 'Unassigned';
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const formatDate = (dateStr) => {
    if (dateStr === 'Unassigned') return 'Today';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handlePlaceOrder = () => {
    setIsProcessing(true);
    setTimeout(() => {
      dispatch(confirmOrder());
      setIsProcessing(false);
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/order-success');
      }, 1500);
    }, 2000);
  };

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      dispatch(clearSelection());
      navigate('/');
    }
  };

  // ✅ Navigate back to TiffinService page
  const handleBack = () => {
    navigate('/tiffin-service');
  };

  // Calculate totals
  const subtotal = totalPrice;
  const gst = subtotal * 0.18;
  const deliveryFee = 10;
  const grandTotal = subtotal + gst + deliveryFee;

  if (!orderItems.length) {
    return (
      <div className="py-4 px-4 max-w-4xl mx-auto min-h-screen" style={{ backgroundColor: colors.background }}>
        <div className="flex items-center gap-4 mb-6">
          <button onClick={handleBack} className="text-2xl" style={{ color: colors.text }}><FaArrowLeft /></button>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>💳 Checkout</h1>
        </div>
        <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <p className="text-4xl mb-4">🛒</p>
          <p className="text-lg" style={{ color: colors.textSecondary }}>Your cart is empty</p>
          <button onClick={handleBack} className="mt-4 px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform" style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}>
            Browse Meals
          </button>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="py-4 px-4 max-w-6xl mx-auto min-h-screen"
      style={{ backgroundColor: colors.background }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* ✅ Updated back button to use handleBack */}
          <button onClick={handleBack} className="text-2xl" style={{ color: colors.text }}>
            <FaArrowLeft />
          </button>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>💳 Checkout</h1>
        </div>
        <button
          className="text-sm font-medium hover:underline"
          style={{ color: colors.danger || '#EF4444' }}
          onClick={handleClearCart}
        >
          Clear Cart
        </button>
      </div>

      {/* Order Progress */}
      <div className="flex items-center justify-center gap-2 mb-6">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}>1</span>
          <span className="text-sm" style={{ color: colors.text }}>Cart</span>
        </div>
        <div className="w-12 h-0.5" style={{ backgroundColor: colors.primary }}></div>
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: colors.primary, color: '#FFFFFF' }}>2</span>
          <span className="text-sm font-semibold" style={{ color: colors.primary }}>Checkout</span>
        </div>
        <div className="w-12 h-0.5" style={{ backgroundColor: colors.border }}></div>
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold" style={{ backgroundColor: colors.border, color: colors.textSecondary }}>3</span>
          <span className="text-sm" style={{ color: colors.textSecondary }}>Confirm</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Section - Order Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items Grouped by Date */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <h3 className="font-bold mb-4" style={{ color: colors.text }}>
              📋 Order Summary
              <span className="text-sm font-normal ml-2" style={{ color: colors.textSecondary }}>
                ({orderItems.length} items)
              </span>
            </h3>
            
            {Object.entries(groupedItems).map(([date, items]) => (
              <div key={date} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ color: colors.primary }}>📅</span>
                  <span className="font-semibold" style={{ color: colors.text }}>
                    {formatDate(date)}
                  </span>
                  <span className="text-xs" style={{ color: colors.textSecondary }}>
                    ({items.length} items)
                  </span>
                </div>
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center py-2 px-3 rounded-lg mb-1" style={{ backgroundColor: colors.inputBackground }}>
                    <div>
                      <p className="font-medium" style={{ color: colors.text }}>
                        {item.name} <span className="text-sm font-normal" style={{ color: colors.textSecondary }}>x{item.quantity}</span>
                      </p>
                      <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                        <span>{item.mealTypeSelected || 'Lunch'}</span>
                        <span>•</span>
                        <span>{item.scheduledTime || '12:00 PM'}</span>
                      </div>
                    </div>
                    <p className="font-bold" style={{ color: colors.primary }}>₹{item.price * item.quantity}</p>
                  </div>
                ))}
              </div>
            ))}

            {/* Delivery Info */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <FaTruck style={{ color: colors.primary }} />
              <div>
                <p className="text-sm font-medium" style={{ color: colors.text }}>Delivery Address</p>
                <p className="text-xs" style={{ color: colors.textSecondary }}>Kalyan City, West Bengal, 712502</p>
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <h3 className="font-bold mb-4" style={{ color: colors.text }}>
              💳 Select Payment Method
            </h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div
                  key={method.id}
                  className={`flex items-center gap-4 p-3 rounded-xl cursor-pointer transition-all hover:scale-[1.02] ${
                    selectedPayment === method.id ? 'border-2' : 'border'
                  }`}
                  style={{ 
                    borderColor: selectedPayment === method.id ? colors.primary : colors.border,
                    backgroundColor: selectedPayment === method.id ? colors.primary + '15' : colors.inputBackground
                  }}
                  onClick={() => setSelectedPayment(method.id)}
                >
                  <span className="text-xl" style={{ color: selectedPayment === method.id ? colors.primary : colors.textSecondary }}>
                    {method.icon}
                  </span>
                  <div className="flex-1">
                    <p className="font-semibold" style={{ color: colors.text }}>{method.label}</p>
                    <p className="text-xs" style={{ color: colors.textSecondary }}>{method.desc}</p>
                  </div>
                  {selectedPayment === method.id && (
                    <FaCheckCircle style={{ color: colors.primary }} />
                  )}
                </div>
              ))}
            </div>

            {/* Secure Payment Badge */}
            <div className="flex items-center justify-center gap-2 mt-4 text-xs" style={{ color: colors.textSecondary }}>
              <FaLock className="text-green-500" />
              <span>Your payment is secure. We use SSL encryption.</span>
            </div>
          </div>
        </div>

        {/* Right Section - Payment Summary */}
        <div className="lg:col-span-1">
          <div className="rounded-2xl p-6 sticky top-24" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
            <h3 className="font-bold mb-4" style={{ color: colors.text }}>
              💰 Payment Summary
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                <span>Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                <span>GST (18%)</span>
                <span>₹{gst.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm" style={{ color: colors.textSecondary }}>
                <span>Delivery Fee</span>
                <span>₹{deliveryFee.toFixed(2)}</span>
              </div>
              <div className="border-t pt-3" style={{ borderColor: colors.border }}>
                <div className="flex justify-between">
                  <span className="font-bold text-lg" style={{ color: colors.text }}>Total</span>
                  <span className="text-2xl font-bold" style={{ color: colors.primary }}>
                    ₹{grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Time Estimate */}
            <div className="flex items-center gap-2 mt-4 p-3 rounded-xl" style={{ backgroundColor: colors.inputBackground }}>
              <FaClock style={{ color: colors.primary }} />
              <div>
                <p className="text-xs font-medium" style={{ color: colors.text }}>Estimated Delivery</p>
                <p className="text-xs" style={{ color: colors.textSecondary }}>30-45 minutes</p>
              </div>
            </div>

            {/* Place Order Button */}
            <button
              className="w-full mt-6 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: isProcessing ? colors.textSecondary : colors.primary,
                color: '#FFFFFF',
                cursor: isProcessing ? 'not-allowed' : 'pointer'
              }}
              onClick={handlePlaceOrder}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin h-5 w-5 mr-2 text-white" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Processing...
                </>
              ) : (
                '✅ Place Order'
              )}
            </button>

            <p className="text-center text-xs mt-3" style={{ color: colors.textSecondary }}>
              By placing order, you agree to our Terms & Conditions
            </p>

            <div className="flex items-center justify-center gap-2 mt-3 text-xs" style={{ color: colors.textSecondary }}>
              <FaShieldAlt className="text-green-500" />
              <span>100% Secure Transaction</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Checkout;
